// src/views/gestionEstablissement/AdminPushNotifications.js
import React, { useEffect, useMemo, useState } from 'react';
import Navbar from '../../components/Navbar';
const API_BASE = 'https://backend.csibon.app/chr/notifications';

/** Petite modal de confirmation sans dépendances */
function ConfirmModal({ open, title, description, onCancel, onConfirm }) {
  if (!open) return null;
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
      backdropFilter: 'blur(4px)'
    }}>
      <div style={{
        width: 'min(520px, 92vw)', borderRadius: 16, background: '#fff',
        boxShadow: '0 25px 50px rgba(0,0,0,0.25)', padding: 32,
        border: '1px solid rgba(0,0,0,0.05)'
      }}>
        <div style={{ 
          fontSize: 20, 
          fontWeight: 700, 
          marginBottom: 12,
          color: '#1a1a1a'
        }}>
          {title || 'Confirmer'}
        </div>
        <div style={{ 
          color: '#6b7280', 
          marginBottom: 24,
          lineHeight: 1.6
        }}>
          {description}
        </div>
        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
          <button
            onClick={onCancel}
            style={{ 
              padding: '12px 20px', 
              borderRadius: 10, 
              border: '2px solid #e5e7eb', 
              background: '#fff', 
              cursor: 'pointer',
              fontWeight: 600,
              color: '#6b7280',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={e => {
              e.target.style.borderColor = '#d1d5db';
              e.target.style.background = '#f9fafb';
            }}
            onMouseLeave={e => {
              e.target.style.borderColor = '#e5e7eb';
              e.target.style.background = '#fff';
            }}
          >
            Annuler
          </button>
          <button
            onClick={onConfirm}
            style={{ 
              padding: '12px 20px', 
              borderRadius: 10, 
              border: 'none', 
              background: 'linear-gradient(135deg, #0f766e 0%, #0d9488 100%)', 
              color: '#fff', 
              fontWeight: 700, 
              cursor: 'pointer',
              boxShadow: '0 4px 12px rgba(15, 118, 110, 0.3)',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={e => {
              e.target.style.transform = 'translateY(-1px)';
              e.target.style.boxShadow = '0 6px 16px rgba(15, 118, 110, 0.4)';
            }}
            onMouseLeave={e => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 4px 12px rgba(15, 118, 110, 0.3)';
            }}
          >
            Confirmer
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminPushNotifications() {
  const [tab, setTab] = useState('all'); // 'all' | 'one'

  // --- Onglet "À tous" ---
  const [titleAll, setTitleAll] = useState('Csibon');
  const [messageAll, setMessageAll] = useState('');
  const [sendingAll, setSendingAll] = useState(false);
  const [resultAll, setResultAll] = useState(null);
  const [errorAll, setErrorAll] = useState(null);
  const [confirmAllOpen, setConfirmAllOpen] = useState(false);

  // --- Onglet "Un utilisateur" ---
  const [query, setQuery] = useState('');
  const [onlyWithToken, setOnlyWithToken] = useState(true);
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [selectedUser, setSelectedUser] = useState(null);
  const [titleOne, setTitleOne] = useState('Csibon');
  const [messageOne, setMessageOne] = useState('');
  const [sendingOne, setSendingOne] = useState(false);
  const [resultOne, setResultOne] = useState(null);
  const [errorOne, setErrorOne] = useState(null);

  async function fetchUsers(p = 1) {
    setLoadingUsers(true);
    setErrorOne(null);
    try {
      const url = new URL(`${API_BASE}/users`);
      if (query) url.searchParams.set('q', query);
      url.searchParams.set('has_token', String(onlyWithToken));
      url.searchParams.set('limit', String(limit));
      url.searchParams.set('page', String(p));
      const resp = await fetch(url.toString());
      const data = await resp.json();
      if (!resp.ok) throw new Error(data?.error || 'Erreur réseau');
      setUsers(data.users || []);
      setTotal(data.total || 0);
      setPage(data.page || p);
    } catch (e) {
      setUsers([]);
      setTotal(0);
      setErrorOne(e.message || 'Erreur lors du chargement des utilisateurs.');
    } finally {
      setLoadingUsers(false);
    }
  }

  useEffect(() => {
    if (tab === 'one') fetchUsers(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  // --- Envoi à tous (avec modal de confirmation) ---
  async function doSendAll() {
    setSendingAll(true);
    setErrorAll(null);
    setResultAll(null);
    try {
      const resp = await fetch(`${API_BASE}/send-to-all`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: titleAll || 'Csibon',
          message: messageAll,
          only_with_token: true
        })
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data?.error || 'Erreur envoi');
      setResultAll(data);
      setMessageAll('');
    } catch (e) {
      setErrorAll(e.message || 'Erreur lors de l\'envoi.');
    } finally {
      setSendingAll(false);
      setConfirmAllOpen(false);
    }
  }

  function handleSendAllClick() {
    if (!messageAll.trim()) {
      setErrorAll('Le message est obligatoire.');
      return;
    }
    setConfirmAllOpen(true);
  }

  // --- Envoi à un utilisateur ---
  async function handleSendOne() {
    setErrorOne(null);
    setResultOne(null);

    if (!selectedUser) {
      setErrorOne("Sélectionne un utilisateur.");
      return;
    }
    if (!messageOne.trim()) {
      setErrorOne("Le message est obligatoire.");
      return;
    }

    setSendingOne(true);
    try {
      const resp = await fetch(`${API_BASE}/send-to-user`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: selectedUser.id,
          title: titleOne || 'Csibon',
          message: messageOne
        })
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data?.error || 'Erreur envoi');
      setResultOne(data);
      setMessageOne('');
    } catch (e) {
      setErrorOne(e.message || 'Erreur lors de l\'envoi.');
    } finally {
      setSendingOne(false);
    }
  }

  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / limit)), [total, limit]);

  return (
    <>
      {/* Navbar prend toute la largeur */}
      <div style={{ width: '100%' }}>
        <Navbar />
      </div>
      
      {/* Contenu principal avec background moderne */}
      <div style={{ 
        minHeight: 'calc(100vh - 80px)', 
        background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
        padding: '32px 0'
      }}>
        <div style={{ 
          maxWidth: 1200, 
          margin: '0 auto', 
          padding: '0 24px'
        }}>
          {/* En-tête avec design moderne */}
          <div style={{ 
            background: 'rgba(255,255,255,0.9)', 
            borderRadius: 20, 
            padding: '32px 40px',
            marginBottom: 32,
            boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255,255,255,0.2)'
          }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 16,
              marginBottom: 16
            }}>
              <div style={{
                width: 48,
                height: 48,
                borderRadius: 12,
                background: 'linear-gradient(135deg, #0f766e 0%, #0d9488 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 20
              }}>
                🔔
              </div>
              <div>
                <h1 style={{ 
                  margin: 0, 
                  fontSize: 28, 
                  fontWeight: 800,
                  background: 'linear-gradient(135deg, #1a1a1a 0%, #4a5568 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }}>
                  Notifications Push
                </h1>
                <p style={{ 
                  margin: 0, 
                  color: '#6b7280', 
                  fontSize: 16,
                  fontWeight: 500
                }}>
                  Gérez l'envoi de notifications à vos utilisateurs
                </p>
              </div>
            </div>

            {/* Onglets modernes */}
            <div style={{ 
              display: 'flex', 
              gap: 4, 
              background: '#f1f5f9',
              padding: 4,
              borderRadius: 12,
              width: 'fit-content'
            }}>
              <button
                onClick={() => setTab('all')}
                style={{ 
                  padding: '12px 24px', 
                  borderRadius: 8, 
                  fontWeight: 600,
                  fontSize: 14,
                  border: 'none',
                  background: tab === 'all' ? '#fff' : 'transparent', 
                  color: tab === 'all' ? '#0f766e' : '#64748b', 
                  cursor: 'pointer',
                  boxShadow: tab === 'all' ? '0 2px 8px rgba(0,0,0,0.1)' : 'none',
                  transition: 'all 0.2s ease'
                }}
              >
                📢 À tous les utilisateurs
              </button>
              <button
                onClick={() => setTab('one')}
                style={{ 
                  padding: '12px 24px', 
                  borderRadius: 8, 
                  fontWeight: 600,
                  fontSize: 14,
                  border: 'none',
                  background: tab === 'one' ? '#fff' : 'transparent', 
                  color: tab === 'one' ? '#0f766e' : '#64748b', 
                  cursor: 'pointer',
                  boxShadow: tab === 'one' ? '0 2px 8px rgba(0,0,0,0.1)' : 'none',
                  transition: 'all 0.2s ease'
                }}
              >
                👤 Un utilisateur
              </button>
            </div>
          </div>

          {/* Contenu des onglets */}
          {tab === 'all' && (
            <div style={{ 
              background: 'rgba(255,255,255,0.9)', 
              borderRadius: 20, 
              padding: 32,
              boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255,255,255,0.2)'
            }}>
              <div style={{ marginBottom: 24 }}>
                <label style={{ 
                  display: 'block', 
                  fontWeight: 700, 
                  marginBottom: 8,
                  color: '#1a1a1a',
                  fontSize: 16
                }}>
                  📝 Titre de la notification
                </label>
                <input
                  type="text"
                  value={titleAll}
                  onChange={e => setTitleAll(e.target.value)}
                  placeholder="Csibon"
                  style={{ 
                    width: '100%', 
                    padding: '16px 20px', 
                    borderRadius: 12, 
                    border: '2px solid #e5e7eb',
                    fontSize: 16,
                    fontWeight: 500,
                    transition: 'all 0.2s ease',
                    outline: 'none'
                  }}
                  onFocus={e => e.target.style.borderColor = '#0f766e'}
                  onBlur={e => e.target.style.borderColor = '#e5e7eb'}
                />
              </div>

              <div style={{ marginBottom: 24 }}>
                <label style={{ 
                  display: 'block', 
                  fontWeight: 700, 
                  marginBottom: 8,
                  color: '#1a1a1a',
                  fontSize: 16
                }}>
                  💬 Message
                </label>
                <textarea
                  value={messageAll}
                  onChange={e => setMessageAll(e.target.value)}
                  placeholder="Rédigez votre message pour tous les utilisateurs..."
                  rows={5}
                  style={{ 
                    width: '100%', 
                    padding: '16px 20px', 
                    borderRadius: 12, 
                    border: '2px solid #e5e7eb',
                    fontSize: 16,
                    fontWeight: 500,
                    resize: 'vertical',
                    minHeight: 120,
                    transition: 'all 0.2s ease',
                    outline: 'none',
                    lineHeight: 1.6
                  }}
                  onFocus={e => e.target.style.borderColor = '#0f766e'}
                  onBlur={e => e.target.style.borderColor = '#e5e7eb'}
                />
              </div>

              {errorAll && (
                <div style={{ 
                  marginBottom: 20, 
                  color: '#dc2626', 
                  fontWeight: 600,
                  background: '#fef2f2',
                  padding: '12px 16px',
                  borderRadius: 8,
                  border: '1px solid #fecaca'
                }}>
                  ⚠️ {errorAll}
                </div>
              )}

              <button
                onClick={handleSendAllClick}
                disabled={sendingAll}
                style={{ 
                  padding: '16px 32px', 
                  borderRadius: 12, 
                  fontWeight: 700,
                  fontSize: 16,
                  border: 'none',
                  background: sendingAll 
                    ? 'linear-gradient(135deg, #9ca3af 0%, #6b7280 100%)' 
                    : 'linear-gradient(135deg, #0f766e 0%, #0d9488 100%)', 
                  color: '#fff', 
                  cursor: sendingAll ? 'not-allowed' : 'pointer',
                  boxShadow: '0 8px 20px rgba(15, 118, 110, 0.3)',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8
                }}
                onMouseEnter={e => {
                  if (!sendingAll) {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 12px 24px rgba(15, 118, 110, 0.4)';
                  }
                }}
                onMouseLeave={e => {
                  if (!sendingAll) {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 8px 20px rgba(15, 118, 110, 0.3)';
                  }
                }}
              >
                {sendingAll ? '⏳ Envoi en cours...' : '🚀 Envoyer à tous'}
              </button>

              {resultAll && (
                <div style={{ 
                  marginTop: 24, 
                  background: '#f0fdf4', 
                  padding: 20, 
                  borderRadius: 12,
                  border: '1px solid #bbf7d0'
                }}>
                  <div style={{ 
                    color: '#166534', 
                    fontWeight: 700, 
                    marginBottom: 8,
                    fontSize: 16
                  }}>
                    ✅ Notification envoyée avec succès !
                  </div>
                  <pre style={{ 
                    margin: 0, 
                    whiteSpace: 'pre-wrap',
                    fontSize: 14,
                    color: '#15803d',
                    background: 'rgba(255,255,255,0.5)',
                    padding: 12,
                    borderRadius: 8
                  }}>
                    {JSON.stringify(resultAll, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          )}

          {tab === 'one' && (
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: '1fr 1fr', 
              gap: 32,
              alignItems: 'start'
            }}>
              {/* Panel de recherche d'utilisateurs */}
              <div style={{ 
                background: 'rgba(255,255,255,0.9)', 
                borderRadius: 20, 
                padding: 24,
                boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255,255,255,0.2)'
              }}>
                <h3 style={{ 
                  margin: '0 0 20px 0', 
                  color: '#1a1a1a',
                  fontSize: 18,
                  fontWeight: 700
                }}>
                  🔍 Rechercher un utilisateur
                </h3>

                <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
                  <input
                    type="text"
                    value={query}
                    onChange={e => setQuery(e.target.value)}
                    placeholder="Email, pseudo, nom, téléphone..."
                    style={{ 
                      flex: 1, 
                      padding: '12px 16px', 
                      borderRadius: 10, 
                      border: '2px solid #e5e7eb',
                      fontSize: 14,
                      outline: 'none',
                      transition: 'all 0.2s ease'
                    }}
                    onFocus={e => e.target.style.borderColor = '#0f766e'}
                    onBlur={e => e.target.style.borderColor = '#e5e7eb'}
                  />
                  <button
                    onClick={() => fetchUsers(1)}
                    style={{ 
                      padding: '12px 20px', 
                      borderRadius: 10, 
                      fontWeight: 600,
                      border: 'none',
                      background: 'linear-gradient(135deg, #0f766e 0%, #0d9488 100%)',
                      color: '#fff',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    🔍
                  </button>
                </div>

                <label style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 8, 
                  marginBottom: 16,
                  cursor: 'pointer',
                  color: '#4b5563',
                  fontWeight: 500
                }}>
                  <input
                    type="checkbox"
                    checked={onlyWithToken}
                    onChange={e => setOnlyWithToken(e.target.checked)}
                    style={{ transform: 'scale(1.2)' }}
                  />
                  📱 Uniquement avec token Expo
                </label>

                {/* Pagination */}
                <div style={{ 
                  margin: '16px 0', 
                  display: 'flex', 
                  gap: 12, 
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: '#f8fafc',
                  padding: '12px 16px',
                  borderRadius: 10
                }}>
                  <button
                    disabled={loadingUsers || page <= 1}
                    onClick={() => fetchUsers(page - 1)}
                    style={{ 
                      cursor: page <= 1 ? 'not-allowed' : 'pointer',
                      background: page <= 1 ? '#e5e7eb' : '#0f766e',
                      color: page <= 1 ? '#9ca3af' : '#fff',
                      border: 'none',
                      padding: '8px 12px',
                      borderRadius: 6,
                      fontWeight: 600
                    }}
                  >
                    ◀
                  </button>
                  <div style={{ 
                    fontWeight: 600,
                    color: '#374151',
                    fontSize: 14
                  }}>
                    Page {page} sur {totalPages} ({total} utilisateurs)
                  </div>
                  <button
                    disabled={loadingUsers || page >= totalPages}
                    onClick={() => fetchUsers(page + 1)}
                    style={{ 
                      cursor: page >= totalPages ? 'not-allowed' : 'pointer',
                      background: page >= totalPages ? '#e5e7eb' : '#0f766e',
                      color: page >= totalPages ? '#9ca3af' : '#fff',
                      border: 'none',
                      padding: '8px 12px',
                      borderRadius: 6,
                      fontWeight: 600
                    }}
                  >
                    ▶
                  </button>
                </div>

                {/* Liste des utilisateurs */}
                <div style={{ 
                  maxHeight: 400, 
                  overflow: 'auto', 
                  border: '2px solid #f1f5f9', 
                  borderRadius: 12,
                  background: '#fefefe'
                }}>
                  {loadingUsers ? (
                    <div style={{ 
                      padding: 24, 
                      textAlign: 'center',
                      color: '#6b7280'
                    }}>
                      ⏳ Chargement...
                    </div>
                  ) : users.length === 0 ? (
                    <div style={{ 
                      padding: 24, 
                      textAlign: 'center',
                      color: '#6b7280'
                    }}>
                      🔍 Aucun utilisateur trouvé
                    </div>
                  ) : (
                    users.map((u, index) => (
                      <label 
                        key={u.id} 
                        style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 12, 
                          padding: '16px 20px', 
                          borderBottom: index < users.length - 1 ? '1px solid #f1f5f9' : 'none', 
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          background: selectedUser?.id === u.id ? '#f0fdf4' : 'transparent'
                        }}
                        onMouseEnter={e => {
                          if (selectedUser?.id !== u.id) {
                            e.target.style.background = '#f8fafc';
                          }
                        }}
                        onMouseLeave={e => {
                          if (selectedUser?.id !== u.id) {
                            e.target.style.background = 'transparent';
                          }
                        }}
                      >
                        <input
                          type="radio"
                          name="selectedUser"
                          checked={selectedUser?.id === u.id}
                          onChange={() => setSelectedUser(u)}
                          style={{ transform: 'scale(1.3)', accentColor: '#0f766e' }}
                        />
                        <div style={{ flex: 1 }}>
                          <div style={{ 
                            fontWeight: 700,
                            color: '#1a1a1a',
                            marginBottom: 4
                          }}>
                            {u.email} {u.pseudo ? `• ${u.pseudo}` : ''}
                          </div>
                          <div style={{ 
                            fontSize: 12, 
                            color: '#6b7280',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            flexWrap: 'wrap'
                          }}>
                            <span>{u.first_name || '-'} {u.name || ''}</span>
                            {u.num_phone && <span>📞 {u.num_phone}</span>}
                            <span style={{
                              background: u.has_push_token ? '#dcfce7' : '#fef2f2',
                              color: u.has_push_token ? '#166534' : '#dc2626',
                              padding: '2px 8px',
                              borderRadius: 6,
                              fontSize: 11,
                              fontWeight: 600
                            }}>
                              {u.has_push_token ? '✅ Token OK' : '❌ Pas de token'}
                            </span>
                          </div>
                        </div>
                      </label>
                    ))
                  )}
                </div>
              </div>

              {/* Panel d'envoi */}
              <div style={{ 
                background: 'rgba(255,255,255,0.9)', 
                borderRadius: 20, 
                padding: 24,
                boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255,255,255,0.2)'
              }}>
                <h3 style={{ 
                  margin: '0 0 20px 0', 
                  color: '#1a1a1a',
                  fontSize: 18,
                  fontWeight: 700
                }}>
                  💌 Envoyer une notification
                </h3>

                <div style={{ marginBottom: 20 }}>
                  <label style={{ 
                    display: 'block', 
                    fontWeight: 600, 
                    marginBottom: 8,
                    color: '#374151',
                    fontSize: 14
                  }}>
                    📝 Titre
                  </label>
                  <input
                    type="text"
                    value={titleOne}
                    onChange={e => setTitleOne(e.target.value)}
                    placeholder="Csibon"
                    style={{ 
                      width: '100%', 
                      padding: '12px 16px', 
                      borderRadius: 10, 
                      border: '2px solid #e5e7eb',
                      fontSize: 14,
                      outline: 'none',
                      transition: 'all 0.2s ease'
                    }}
                    onFocus={e => e.target.style.borderColor = '#0f766e'}
                    onBlur={e => e.target.style.borderColor = '#e5e7eb'}
                  />
                </div>

                <div style={{ marginBottom: 20 }}>
                  <label style={{ 
                    display: 'block', 
                    fontWeight: 600, 
                    marginBottom: 8,
                    color: '#374151',
                    fontSize: 14
                  }}>
                    💬 Message
                  </label>
                  <textarea
                    value={messageOne}
                    onChange={e => setMessageOne(e.target.value)}
                    placeholder="Votre message personnalisé..."
                    rows={6}
                    style={{ 
                      width: '100%', 
                      padding: '12px 16px', 
                      borderRadius: 10, 
                      border: '2px solid #e5e7eb',
                      fontSize: 14,
                      resize: 'vertical',
                      minHeight: 120,
                      outline: 'none',
                      transition: 'all 0.2s ease',
                      lineHeight: 1.6
                    }}
                    onFocus={e => e.target.style.borderColor = '#0f766e'}
                    onBlur={e => e.target.style.borderColor = '#e5e7eb'}
                  />
                </div>

                {selectedUser && (
                  <div style={{
                    background: '#f0f9ff',
                    border: '1px solid #bae6fd',
                    borderRadius: 10,
                    padding: 16,
                    marginBottom: 20
                  }}>
                    <div style={{ 
                      fontSize: 12, 
                      fontWeight: 700, 
                      color: '#0369a1',
                      marginBottom: 4
                    }}>
                      👤 Destinataire sélectionné :
                    </div>
                    <div style={{ 
                      color: '#0c4a6e',
                      fontWeight: 600
                    }}>
                      {selectedUser.email} {selectedUser.pseudo ? `(${selectedUser.pseudo})` : ''}
                    </div>
                  </div>
                )}

                {errorOne && (
                  <div style={{ 
                    marginBottom: 16, 
                    color: '#dc2626', 
                    fontWeight: 600,
                    background: '#fef2f2',
                    padding: '12px 16px',
                    borderRadius: 8,
                    border: '1px solid #fecaca',
                    fontSize: 14
                  }}>
                    ⚠️ {errorOne}
                  </div>
                )}

                <button
                  onClick={handleSendOne}
                  disabled={sendingOne || !selectedUser}
                  style={{ 
                    width: '100%',
                    padding: '14px 24px', 
                    borderRadius: 10, 
                    fontWeight: 700,
                    fontSize: 14,
                    border: 'none',
                    background: (sendingOne || !selectedUser)
                      ? 'linear-gradient(135deg, #9ca3af 0%, #6b7280 100%)' 
                      : 'linear-gradient(135deg, #0f766e 0%, #0d9488 100%)', 
                    color: '#fff', 
                    cursor: (sendingOne || !selectedUser) ? 'not-allowed' : 'pointer',
                    boxShadow: '0 6px 16px rgba(15, 118, 110, 0.3)',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 8
                  }}
                  onMouseEnter={e => {
                    if (!sendingOne && selectedUser) {
                      e.target.style.transform = 'translateY(-1px)';
                      e.target.style.boxShadow = '0 8px 20px rgba(15, 118, 110, 0.4)';
                    }
                  }}
                  onMouseLeave={e => {
                    if (!sendingOne && selectedUser) {
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = '0 6px 16px rgba(15, 118, 110, 0.3)';
                    }
                  }}
                >
                  {sendingOne ? '⏳ Envoi...' : !selectedUser ? '👤 Sélectionnez un utilisateur' : '📨 Envoyer'}
                </button>

                {resultOne && (
                  <div style={{ 
                    marginTop: 20, 
                    background: '#f0fdf4', 
                    padding: 16, 
                    borderRadius: 10,
                    border: '1px solid #bbf7d0'
                  }}>
                    <div style={{ 
                      color: '#166534', 
                      fontWeight: 700, 
                      marginBottom: 8,
                      fontSize: 14
                    }}>
                      ✅ Notification envoyée !
                    </div>
                    <pre style={{ 
                      margin: 0, 
                      whiteSpace: 'pre-wrap',
                      fontSize: 12,
                      color: '#15803d',
                      background: 'rgba(255,255,255,0.6)',
                      padding: 12,
                      borderRadius: 6,
                      maxHeight: 150,
                      overflow: 'auto'
                    }}>
                      {JSON.stringify(resultOne, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Modal de confirmation pour l'envoi à tous */}
          <ConfirmModal
            open={confirmAllOpen}
            title="Envoyer à tous les utilisateurs ?"
            description="Vous êtes sur le point d'envoyer une notification push à tous les utilisateurs disposant d'un token Expo valide. Cette action est irréversible."
            onCancel={() => setConfirmAllOpen(false)}
            onConfirm={doSendAll}
          />
        </div>
      </div>
    </>
  );
}